import * as React from "react";
import { CACHE, setEdgeCache } from "@/lib/http-cache";
import { NotFoundSeo } from "@/components/not-found-view";
import { createFileRoute, notFound, Link, stripSearchParams } from "@tanstack/react-router";
import {
  validateCategorySearch,
  isCategorySlug,
  loadCategory,
  categoryHead,
  CategoryView,
  type CategoryLoaderData,
} from "@/lib/category-view";
import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  titleText,
  featuredImage,
  postAuthor,
  postCategories,
  postTags,
  isSponsoredPost,
  formatDate,
  readingTime,
  postReadingTime,
  rewriteWpLinks,
  enhanceContentImages,
  excerptText,
  articlePath,
  decodeHtmlEntities,
  discoverImage,
  type WPPost,
} from "@/lib/wp";

import { SITE } from "@/lib/site";
import { resolveCountrySlug } from "@/lib/countries";
import { countryLabelForSlug } from "@/lib/taxonomy";
import { normalizeArticleSlug, canonicalArticleUrl } from "@/lib/article-slug";
import { resolveCanonicalSlug } from "@/lib/article-canonical-overrides";
import { recordArticleRead } from "@/lib/reading-signals";
import { recordRecentRead } from "@/lib/recent-reads";
import { ArticleCard } from "@/components/article-card";
import { SmartImage } from "@/components/smart-image";
import { AdSlot } from "@/components/ad-slot";
import { BriefInviteCard } from "@/components/brief-invite-card";

import { ConversionModule } from "@/components/conversion-module";
import { Calendar, Clock, MapPin, Share2, ShieldCheck } from "lucide-react";
import { logShare, type ShareChannel } from "@/lib/share-log";
import { ArticleMeta } from "@/components/article-meta";
import { ReadingProgress } from "@/components/reading-progress";

import { ArticleToc, type TocEntry } from "@/components/article-toc";
import { InlineRelated } from "@/components/inline-related";
import { BookmarkButton } from "@/components/bookmark-button";
import { ShareRail } from "@/components/share-rail";
import { AuthorBio } from "@/components/author-bio";

import { TitleRule } from "@/components/title-rule";
import { CompanyContextCard } from "@/components/company-context-card";


import { useArticleTracking } from "@/lib/use-article-tracking";
import { extractFaq, extractToc, splitIntoParagraphChunks, splitAfterParagraph, stripFaqBlock, extractPullQuote, type FaqItem } from "@/lib/article-html";
import { ArticleFaq } from "@/components/article-faq";
import { FALLBACK_VIRTUAL_AUTHORS, listVirtualAuthors, pickVirtualAuthor, avatarPublicUrl } from "@/lib/virtual-authors";
import { logRelatedClick, type RelatedModule } from "@/lib/related-log";
import { getSiteSettings } from "@/lib/site-settings.functions";
import { getArticleAiMeta } from "@/lib/article-ai-meta.functions";
import { getArticleAiMetaCached } from "@/lib/article-ai-meta-cache.functions";
import { getArticleFaqCached, generateArticleFaq } from "@/lib/article-faq.functions";
import {
  getArticleInsightCached,
  generateArticleInsight,
  getArticleContext,
} from "@/lib/article-insight.functions";
import { WhyItMatters } from "@/components/article/why-it-matters";
import { EditorialTake } from "@/components/article/editorial-take";


import { PullQuote } from "@/components/article/pull-quote";
import { TrackerBadge } from "@/components/article/tracker-badge";
import { ReaderPulse } from "@/components/article/reader-pulse";
import { handleArticleImageError } from "@/lib/image-fallback";
import { getArticleMetaOverride } from "@/lib/article-meta-overrides";
import { ArticleLimitGate } from "@/components/article-limit-gate";
import { useAnonymousLimits } from "@/lib/use-anonymous-limits";
import { recordAnonymousArticle } from "@/lib/anonymous-limits";
import { supabase } from "@/integrations/supabase/client";

import {
  articleQueryOptions as articleQuery,
  relatedQueryOptions as relatedQuery,
  prefetchArticle,
  useViewportPrefetchList,
} from "@/lib/prefetch-article";



function applyTemplate(tpl: string | null | undefined, vars: Record<string, string>): string {
  if (!tpl) return vars.title ?? "";
  return tpl.replace(/%(\w+)%/g, (_, k) => (vars[k] ?? "")).replace(/\s+—\s+$/, "").replace(/\s+\|\s+$/, "").trim();
}

export const Route = createFileRoute("/$slug")({
  validateSearch: validateCategorySearch,
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  loaderDeps: ({ search }) => ({
    page: search.page,
    country: search.country,
    month: search.month,
    sub: search.sub,
    author: search.author,
    sort: search.sort,
    range: search.range,
  }),
  loader: async ({ params, deps, context }) => {
    // Legacy WordPress slugs may contain percent-encoded non-ASCII
    // characters; decode the segment exactly once and refuse anything that
    // could escape the path segment.
    const slug = normalizeArticleSlug(params.slug);
    if (!slug) throw notFound();

    // Sections (formerly /category/<slug>) share this route with articles.
    if (await isCategorySlug(context.queryClient, slug)) {
      const category = await loadCategory(context.queryClient, slug, deps);
      return { kind: "category" as const, category, post: null, settings: null, aiDescription: null };
    }

    // 1) Cache-first: if the query cache already holds this post (warm worker
    //    or client preload), render instantly with zero network.
    const cachedPost = context.queryClient.getQueryData<WPPost | null>(
      articleQuery(slug).queryKey,
    );

    // 2) Cold start: race the post fetch against a deadline. If WordPress is
    //    slow we ship the shell immediately and the browser finishes the
    //    fetch through the same-origin proxy. Non-critical work (site
    //    settings, AI meta cache) gets a much shorter deadline and can never
    //    hold up the critical route.
    let resolvedMissing = false; // fetch completed and the post truly is gone
    const postP: Promise<WPPost | null> =
      cachedPost !== undefined
        ? Promise.resolve(cachedPost)
        : (context.queryClient.ensureQueryData(articleQuery(slug)) as Promise<WPPost | null>)
            .then((p) => {
              if (!p) resolvedMissing = true;
              return p ?? null;
            })
            .catch((err) => {
              // Upstream unreachable — degrade instead of returning a 500.
              console.error(`[article] SSR fetch failed for ${slug}:`, (err as Error).message);
              return null;
            });

    const withDeadline = <T,>(p: Promise<T>, ms: number): Promise<T | null> =>
      Promise.race([p, new Promise<null>((r) => setTimeout(() => r(null), ms))]);

    const settingsP = getSiteSettings().catch(() => null);
    const metaCacheP = getArticleAiMetaCached({ data: { slug: slug } }).catch(() => null);
    const faqCacheP = getArticleFaqCached({ data: { slug: slug } }).catch(() => null);
    const insightCacheP = getArticleInsightCached({ data: { slug: slug } }).catch(() => null);

    const [post, settings, metaCached, faqCached, insightCached] = await Promise.all([
      cachedPost !== undefined ? postP : withDeadline(postP, 3500),
      withDeadline(settingsP, 400),
      withDeadline(metaCacheP, 400),
      withDeadline(faqCacheP, 400),
      withDeadline(insightCacheP, 400),
    ]);

    if (!post && resolvedMissing) throw notFound();
    await setEdgeCache(post ? CACHE.article : CACHE.degraded);
    if (!post) {
      // Render the skeleton shell; the browser retries via the proxy.
      return {
        kind: "article" as const,
        category: null,
        post: null,
        settings,
        aiDescription: null,
        generatedFaq: [],
        insight: null,
      };
    }



    // Warm related posts only on the client — on SSR this would add an extra
    // upstream round-trip to the critical route for below-the-fold content.
    if (typeof window !== "undefined") {
      void context.queryClient.prefetchQuery(relatedQuery(slug, post));

      // Company/topic card: resolve the entity here (pure, no network) and
      // prefetch exactly the one payload this article needs, so the card is
      // already populated when it mounts instead of starting its own waterfall.
      void (async () => {
        try {
          const { articleCardContextQuery } = await import("@/lib/company-context.queries");
          const category = postCategories(post)[0] ?? null;
          await context.queryClient.prefetchQuery(
            articleCardContextQuery({
              articleSlug: slug,
              title: titleText(post),
              tags: postTags(post),
              category: category ? { name: category.name, slug: category.slug } : null,
            }),
          );
        } catch {
          /* the card falls back to fetching on mount */
        }
      })();
    }




    // If the AI cache was empty and the WP excerpt is thin, fire the AI
    // generation off in the background so the NEXT visitor benefits. Do NOT
    // await — this used to add 1–3s to every cold article render.
    if (
      typeof window === "undefined" &&
      !metaCached?.description &&
      settings?.auto_generate_meta_description !== false
    ) {
      const wpExcerpt = excerptText(post, 200);
      if (!wpExcerpt || wpExcerpt.length < 80) {
        void getArticleAiMeta({ data: { slug: slug } }).catch(() => {});
      }
    }

    // No editorial Q&A in the body and nothing cached yet? Generate one in the
    // background so this article gets its Questions & Answers block (and the
    // FAQPage markup) on the next render. Never awaited.
    if (
      typeof window === "undefined" &&
      !(faqCached && faqCached.length) &&
      !extractFaq(post.content?.rendered ?? "").length
    ) {
      void generateArticleFaq({ data: { slug: slug } }).catch(() => {});
    }

    // Same pattern for the "Why it matters" box and the reader poll: write it
    // in the background so the next render (and the edge cache) carries it.
    if (typeof window === "undefined" && !(insightCached?.bullets?.length)) {
      void generateArticleInsight({ data: { slug: slug } }).catch(() => {});
    }

    return {
      kind: "article" as const,
      category: null,
      post,
      settings,
      aiDescription: metaCached?.description ?? null,
      generatedFaq: faqCached ?? [],
      insight: insightCached ?? null,
    };
  },
  pendingMs: 0,
  pendingMinMs: 200,
  pendingComponent: ArticlePending,


  head: ({ loaderData, params }) => {
    if (loaderData?.kind === "category") {
      return categoryHead(loaderData.category as CategoryLoaderData, params.slug);
    }
    if (!loaderData || !loaderData.post) {
      // Degraded render (upstream slow) vs. genuine not-found. A degraded
      // shell must never lose the URL's identity: it keeps a self-referential
      // canonical so a transient WordPress timeout cannot strip the article's
      // ranking signals, and it is `noindex, follow` (never shared-cached, see
      // CACHE.degraded) so the empty shell itself is not indexed.
      const selfUrl = canonicalArticleUrl(SITE.url, params.slug);
      const prettySlug = (params.slug || "")
        .split("-")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      if (!loaderData) {
        return {
          meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }],
        };
      }
      return {
        meta: [
          { title: `${prettySlug} | RetailNews Asia` },
          { name: "robots", content: "noindex, follow" },
          { property: "og:url", content: selfUrl },
        ],
        links: [{ rel: "canonical", href: selfUrl }],
      };
    }

    const { post, settings, aiDescription } = loaderData;

    const override = getArticleMetaOverride(post.slug || params.slug);

    const title = override?.title ?? titleText(post);
    const wpDescription = excerptText(post, 155);
    const description =
      override?.description ??
      ((wpDescription && wpDescription.length >= 80 ? wpDescription : null) ??
        aiDescription ??
        wpDescription ??
        settings?.default_description ??
        "");
    const img = featuredImage(post);
    const author = postAuthor(post);
    const cats = postCategories(post);
    const tags = postTags(post);
    const url = canonicalArticleUrl(
      SITE.url,
      resolveCanonicalSlug(post.slug || params.slug),
    );

    const templatedTitle =
      override?.title ??
      (applyTemplate(
        settings?.article_title_template,
        {
          title,
          category: cats[0]?.name ?? "",
          sitename: settings?.organization_name ?? SITE.name,
        },
      ) || `${title} - ${SITE.name}`);


    const tagKeywords = tags.map((t) => decodeHtmlEntities(t.name)).join(", ");
    const keywords = [tagKeywords, settings?.default_keywords ?? ""]
      .filter((s) => s && s.trim())
      .join(", ");

    const robotsDirectives = settings?.robots_meta_directives?.trim()
      || "max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    const ogImage = discoverImage(img?.src, 1600) ?? settings?.default_og_image ?? null;
    const twitterImage = discoverImage(img?.src, 1200) ?? settings?.default_og_image ?? null;
    const twitterHandle = settings?.twitter_handle ?? SITE.twitter;

    const meta = [
      { title: templatedTitle },
      { name: "description", content: description },
      { name: "robots", content: `index,follow,${robotsDirectives}` },
      ...(keywords ? [{ name: "keywords", content: keywords }] : []),
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "article:published_time", content: post.date_gmt + "Z" },
      { property: "article:modified_time", content: post.modified_gmt + "Z" },
      ...(author ? [{ property: "article:author", content: decodeHtmlEntities(author.name) }] : []),
      ...cats.map((c) => ({ property: "article:section", content: decodeHtmlEntities(c.name) })),
      ...tags.map((t) => ({ property: "article:tag", content: decodeHtmlEntities(t.name) })),

      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: twitterHandle },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];
    if (ogImage) {
      meta.push({ property: "og:image", content: ogImage });
    }
    if (twitterImage) {
      meta.push({ name: "twitter:image", content: twitterImage });
    }

    const plainText = (post.content?.rendered || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const wordCount = plainText ? plainText.split(" ").length : undefined;

    const ld = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "@id": `${url}#article`,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      url,
      headline: title.slice(0, 110),
      description,
      image: img?.src
        ? [{
            "@type": "ImageObject",
            url: discoverImage(img.src, 1200) ?? img.src,
            contentUrl: discoverImage(img.src, 1200) ?? img.src,
            caption: img.alt,
            ...(img.width ? { width: img.width } : {}),
            ...(img.height ? { height: img.height } : {}),
          }]
        : undefined,
      datePublished: post.date_gmt + "Z",
      dateModified: post.modified_gmt + "Z",
      inLanguage: SITE.locale.replace("_", "-"),
      isAccessibleForFree: true,
      wordCount,
      author: author
        ? {
            "@type": "Person",
            name: decodeHtmlEntities(author.name),
            url: `${SITE.url}/author/${author.slug}`,
          }
        : undefined,
      publisher: { "@id": `${SITE.url}#organization` },
      articleSection: cats.map((c) => decodeHtmlEntities(c.name)),
      keywords: tags.map((t) => decodeHtmlEntities(t.name)).join(", ") || undefined,

      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", ".article-lead"],
      },
    };

    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.url + "/" },
        ...(cats[0]
          ? [
              {
                "@type": "ListItem",
                position: 2,
                name: cats[0].name,
                item: `${SITE.url}/${cats[0].slug}`,
              },
              { "@type": "ListItem", position: 3, name: title, item: url },
            ]
          : [{ "@type": "ListItem", position: 2, name: title, item: url }]),
      ],
    };

    const editorialFaq = extractFaq(post.content?.rendered ?? "");
    const faqItems = editorialFaq.length ? editorialFaq : (loaderData.generatedFaq ?? []);
    const faqLd = faqItems.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }
      : null;

    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(ld) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
        ...(faqLd ? [{ type: "application/ld+json", children: JSON.stringify(faqLd) }] : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-editorial py-24 text-center">
      <NotFoundSeo
        title="Article not found (404) | RetailNews Asia"
        description="This story may have been unpublished or the URL changed. Explore the latest retail and e-commerce coverage from Asia."
      />
      <p className="category-eyebrow">404</p>
      <h1 className="mt-3 text-4xl font-bold">Article not found</h1>
      <p className="mt-3 text-ink-muted">This story may have been unpublished or the URL changed.</p>
      <Link
        to="/"
        className="mt-8 inline-flex h-11 items-center bg-foreground px-6 text-sm font-semibold uppercase tracking-wide text-background font-sans"
      >
        Return home
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-editorial py-24 text-center">
      <h1 className="text-3xl font-bold">Couldn't load this article</h1>
      <p className="mt-3 text-ink-muted">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
  component: SlugPage,
});

function ArticlePending() {
  return (
    <div className="container-editorial py-10">
      <div className="w-full">
        <div className="skeleton-shimmer h-4 w-32 rounded" />
        <div className="skeleton-shimmer mt-4 h-10 w-full rounded" />
        <div className="skeleton-shimmer mt-3 h-10 w-4/5 rounded" />
        <div className="skeleton-shimmer mt-6 aspect-[16/9] w-full rounded" />
        <div className="mt-8 space-y-3">
          <div className="skeleton-shimmer h-4 w-full rounded" />
          <div className="skeleton-shimmer h-4 w-11/12 rounded" />
          <div className="skeleton-shimmer h-4 w-10/12 rounded" />
          <div className="skeleton-shimmer h-4 w-9/12 rounded" />
        </div>
      </div>
    </div>
  );
}

// Trim an author bio by ~30%, cutting on a sentence or word boundary so the
// shortened text still reads naturally.
function halveText(input: string): string {
  const text = input.trim();
  if (!text) return text;
  const target = Math.max(80, Math.floor(text.length * 0.7));
  if (text.length <= target) return text;
  const slice = text.slice(0, target);
  const lastSentence = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("! "), slice.lastIndexOf("? "));
  if (lastSentence > target * 0.6) return slice.slice(0, lastSentence + 1);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).replace(/[,;:\s]+$/, "") + "…";
}


function SlugPage() {
  const loaderData = Route.useLoaderData();
  const { slug } = Route.useParams();
  const search = Route.useSearch();

  if (loaderData?.kind === "category" && loaderData.category) {
    return (
      <CategoryView
        data={loaderData.category as CategoryLoaderData}
        page={search.page}
        slug={slug}
      />
    );
  }
  return <ArticlePage />;
}

function ArticlePage() {
  const { post: loadedPost, generatedFaq } = Route.useLoaderData();
  const { slug } = Route.useParams();

  // When SSR couldn't reach WordPress (Cloudflare bot-block, upstream 5xx),
  // the loader returns post: null instead of throwing a 500. Retry from the
  // browser through our same-origin proxy so the reader still gets the story.
  const { data: clientPost } = useQuery({
    ...articleQuery(slug),
    enabled: !loadedPost,
    retry: 4,
    retryDelay: (a) => Math.min(1000 * 2 ** a, 8000),
  });

  const post = loadedPost ?? clientPost ?? null;
  if (!post) return <ArticlePending />;
  return <ArticleView post={post} slug={slug} />;
}

function ArticleView({ post, slug }: { post: WPPost; slug: string }) {
  const loaderData = Route.useLoaderData();
  const generatedFaq = loaderData.generatedFaq ?? [];
  const cachedInsight = loaderData.insight ?? null;
  const { data: relatedRaw = [] } = useQuery(relatedQuery(slug, post));
  // Fallback so "More from the newsroom" is present on every article, even when
  // the topical related lookup comes back empty.
  const { data: latestFallback } = useQuery({
    queryKey: ["article-related-fallback"],
    queryFn: async () => {
      const { getPosts } = await import("@/lib/wp");
      const res = await getPosts({ perPage: 8 });
      return res.items ?? [];
    },
    enabled: relatedRaw.length === 0,
    staleTime: 5 * 60 * 1000,
  });
  const related: WPPost[] = relatedRaw.length
    ? relatedRaw
    : (latestFallback ?? []).filter((p: WPPost) => p.slug !== slug).slice(0, 6);

  const { data: virtualPool = [] } = useQuery({
    queryKey: ["virtual-authors", "active"],
    queryFn: () => listVirtualAuthors(true),
    initialData: FALLBACK_VIRTUAL_AUTHORS,
    // Treat the seed pool as already stale so the full author roster from the
    // backend loads immediately instead of pinning bylines to the 6 fallbacks.
    initialDataUpdatedAt: 0,
    staleTime: 10 * 60 * 1000,
  });

  const virtualAuthor = React.useMemo(
    () => pickVirtualAuthor(virtualPool, post.id),
    [virtualPool, post.id],
  );

  const queryClient = useQueryClient();
  const relatedRailRef = useViewportPrefetchList(related.map((p: WPPost) => p.slug));

  const trackRelated = React.useCallback(
    (module: RelatedModule, position: number, p: WPPost) => {
      const handler = () =>
        logRelatedClick({
          module,
          position,
          fromSlug: slug,
          toSlug: p.slug,
          toTitle: titleText(p),
        });
      // Intent-based warm-up: by the time the click lands the article is cached.
      const warm = () => prefetchArticle(queryClient, p.slug, { immediate: true });
      return {
        onClick: handler,
        onAuxClick: handler,
        onMouseEnter: warm,
        onFocus: warm,
        onTouchStart: warm,
      };
    },
    [slug, queryClient],
  );



  const title = titleText(post);
  const img = featuredImage(post);
  const wpAuthor = postAuthor(post);
  // Prefer a virtual author when the admin has set up a pool.
  const author = virtualAuthor
    ? {
        slug: virtualAuthor.slug,
        name: virtualAuthor.name,
        description: virtualAuthor.bio ?? "",
        avatarUrl: avatarPublicUrl(virtualAuthor.avatar_url),
        title: virtualAuthor.title,
        isVirtual: true as const,
      }
    : wpAuthor
    ? {
        slug: wpAuthor.slug,
        name: wpAuthor.name,
        description: wpAuthor.description ?? "",
        avatarUrl: wpAuthor.avatar_urls?.["96"] ?? null,
        title: null as string | null,
        isVirtual: false as const,
      }
    : null;
  const cats = postCategories(post);
  const tags = postTags(post);
  const primaryCat = cats[0];
  // Factual market signal, derived only from existing country tags. Shown as a
  // small dateline-style link in the byline row; never invented.
  const marketSlug =
    [...tags, ...cats].map((t) => resolveCountrySlug(t.slug)).find((s): s is string => Boolean(s)) ?? null;
  const marketName = marketSlug ? countryLabelForSlug(marketSlug) : null;
  const mins = readingTime(post.content.rendered);
  const rawHtml = enhanceContentImages(rewriteWpLinks(post.content.rendered), title);
  const excerpt = excerptText(post, 240);

  // Local-only interest signal powering the homepage "For you" module and the
  // "Recently read" shelf in My Retail. Never leaves this browser.
  React.useEffect(() => {
    recordArticleRead({
      categories: cats.map((c) => c.slug),
      tags: tags.map((t) => t.slug),
    });
    recordRecentRead({
      slug: post.slug,
      title,
      image: img?.src ?? null,
      category: primaryCat?.name ?? null,
    });
    // Anonymous article counts are tracked locally for the freemium limit.
    void supabase.auth.getUser().then(({ data }) => {
      if (!data.user) recordAnonymousArticle(post.slug);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);




  // Pull the editorial "Questions & Answers" block out of the body so it can be
  // rendered as a real FAQ (and deep-linked from the table of contents).
  const editorialFaq = React.useMemo(() => extractFaq(rawHtml), [rawHtml]);

  // Articles published without an editorial Q&A get an automatically written
  // one (grounded in the body only). The loader serves it from cache; on a
  // cold article we generate it here so this reader sees it too.
  const { data: autoFaq } = useQuery({
    queryKey: ["article-faq", post.slug],
    queryFn: () => generateArticleFaq({ data: { slug: post.slug } }),
    enabled: !editorialFaq.length && !generatedFaq.length,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  // "Why it matters", the signed "What this means" take and the reader poll.
  // Served from the loader cache; on a cold article, or on an older one that
  // predates the take, we regenerate here so this reader already sees the block.
  const { data: liveInsight } = useQuery({
    queryKey: ["article-insight", post.slug],
    queryFn: () => generateArticleInsight({ data: { slug: post.slug } }),
    enabled: !cachedInsight?.bullets?.length || !cachedInsight?.take,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });
  const insight = liveInsight ?? (cachedInsight?.bullets?.length ? cachedInsight : null);


  // Story timeline + tracker badge (client-only, below the fold).
  const { data: articleContext } = useQuery({
    queryKey: ["article-context", post.slug],
    queryFn: () => getArticleContext({ data: { slug: post.slug } }),
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const faq: FaqItem[] = editorialFaq.length
    ? editorialFaq
    : generatedFaq.length
      ? generatedFaq
      : (autoFaq ?? []);
  // Extract table of contents from h2/h3 (adds ids). Then split for inline related.
  const { html: withIds, toc: bodyToc } = React.useMemo(
    () => extractToc(editorialFaq.length ? stripFaqBlock(rawHtml) : rawHtml),
    [rawHtml, editorialFaq.length],
  );
  const toc = React.useMemo<TocEntry[]>(
    () =>
      faq.length
        ? [
            ...bodyToc,
            { id: "faq", text: "Questions & Answers", level: 2 as const },
            ...faq.map((f) => ({ id: f.id, text: f.question, level: 3 as const })),
          ]
        : bodyToc,
    [bodyToc, faq],
  );
  const wordCount = React.useMemo(
    () => post.content.rendered.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length,
    [post.content.rendered],
  );
  // One pull quote lifted from the body, shown after the opening section.
  const pullQuote = React.useMemo(
    () => (wordCount >= 450 ? extractPullQuote(withIds, { skipFirstParagraphs: 2 }) : null),
    [withIds, wordCount],
  );

  // Decide how many inline related-read modules to weave into the body.
  // Short posts get none; longer reads get 1–3, capped by available related posts.
  const desiredInline = wordCount >= 1600 ? 3 : wordCount >= 1000 ? 2 : wordCount >= 600 ? 1 : 0;
  const inlineRelated = related.slice(0, desiredInline);
  const chunks = React.useMemo(
    () =>
      inlineRelated.length > 0
        ? splitIntoParagraphChunks(withIds, inlineRelated.length + 1)
        : [withIds],
    [withIds, inlineRelated.length],
  );
  // The pull quote belongs roughly halfway through the read, not at the very
  // end. Split the first body chunk in two and drop the quote in between.
  const [leadHtml, restLeadHtml] = React.useMemo(() => {
    const first = chunks[0] ?? "";
    if (!pullQuote) return [first, ""] as [string, string];
    const halves = splitIntoParagraphChunks(first, 2);
    return [halves[0] ?? first, halves[1] ?? ""] as [string, string];
  }, [chunks[0], pullQuote]);
  // Anonymous readers who hit the monthly cap see a short teaser before the
  // gate. One paragraph keeps the teaser short and the CTA above the fold.
  const [previewHtml] = React.useMemo(
    () => splitAfterParagraph(chunks[0] ?? "", 1),
    [chunks[0]],
  );

  // Anonymous readers get 10 free articles per calendar month. Signed-in
  // readers are never gated; SSR renders the full article for crawlers.
  const { limited } = useAnonymousLimits();
  const articleLocked = limited.articles;


  // Sponsored content detection — paid partner posts carry a "sponsored"
  // WordPress tag (set on approval) and/or a sponsored category.
  const isSponsored = React.useMemo(() => isSponsoredPost(post), [post]);

  // GA4: publish article dimensions as ambient context + fire article_view /
  // article_read. Every other event on this page inherits these dimensions.
  useArticleTracking(
    React.useMemo(
      () => ({
        slug,
        title,
        category: primaryCat?.name ?? null,
        categories: cats.map((c) => c.name),
        tags: tags.map((t) => t.name),
        author: author?.name ?? null,
        authorType: author ? (author.isVirtual ? ("virtual" as const) : ("wp" as const)) : ("none" as const),
        wordCount,
        readingMinutes: mins,
        publishedAt: post.date ?? null,
        locale: "en",
        sponsored: isSponsored,
      }),
      [slug, title, primaryCat?.name, cats, tags, author, wordCount, mins, post.date, isSponsored],
    ),
  );



  return (
    <>
      <ReadingProgress targetSelector="article" />

      <div className="border-b border-hairline bg-muted/30">
        <article className="container-editorial grid gap-8 py-12 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[80px_minmax(0,1fr)_320px] xl:gap-10">
          <div className="hidden xl:block">
            <ShareRail title={title} slug={slug} />
          </div>

          <div className="min-w-0">
            {isSponsored ? (
              <div className="mb-6 flex items-center gap-2 border-l-4 border-amber-500 bg-amber-500/10 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-amber-900 dark:text-amber-200 font-sans">
                <ShieldCheck className="h-3.5 w-3.5" />
                Sponsored content, paid partner post
              </div>
            ) : null}
            <header className="mx-auto w-full max-w-4xl text-center">
              {primaryCat ? (
                <>
                  <span className="mx-auto mb-4 block h-px w-16 bg-brand" aria-hidden="true" />
                  <Link
                    to="/$slug"
                    params={{ slug: primaryCat.slug }}
                    className="category-eyebrow hover:underline"
                  >
                    {primaryCat.name}
                  </Link>
                </>
              ) : null}
              <h1 className="mt-4 text-[2.5rem] font-bold leading-[1.05] tracking-tight md:text-5xl lg:text-[3.5rem]">
                {title}
              </h1>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-wide text-ink-muted font-sans">
                {author ? (
                  <span className="normal-case tracking-normal text-sm">
                    By{" "}
                    <Link
                      to="/author/$slug"
                      params={{ slug: author.slug }}
                      className="rounded-sm font-semibold text-foreground outline-none hover:text-brand hover:underline focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {author.name}
                    </Link>
                  </span>
                ) : null}
                {marketSlug && marketName ? (
                  <Link
                    to="/markets/$country"
                    params={{ country: marketSlug }}
                    className="inline-flex items-center gap-1.5 rounded-sm font-semibold uppercase tracking-wide outline-none hover:text-brand focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    title={`More retail news from ${marketName}`}
                  >
                    <MapPin className="h-3 w-3 shrink-0 text-brand" aria-hidden="true" />
                    <span>{marketName}</span>
                  </Link>
                ) : null}
                <ArticleMeta post={post} showUpdated showTime className="text-[11px]" />

                <span className="flex items-center gap-3">
                  <ShareButton title={title} />
                  <span className="text-hairline" aria-hidden="true">|</span>
                  <BookmarkButton
                    article={{
                      slug: post.slug,
                      title,
                      image: img?.src,
                      category: primaryCat?.name,
                    }}
                  />
                </span>
              </div>
            </header>

            {img ? (
              <figure className="mx-auto mt-10 w-full max-w-5xl">
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg bg-muted shadow-sm">
                  <img
                    src={img.src}
                    srcSet={img.srcSet || undefined}
                    sizes="(min-width: 1280px) 960px, (min-width: 1024px) 70vw, 100vw"
                    alt={img.alt || title}
                    width={img.width}
                    height={img.height}
                    fetchPriority="high"
                    decoding="async"
                    onError={handleArticleImageError}
                    className="h-full w-full object-cover"
                  />
                </div>
                {img.caption ? (
                  <figcaption className="mt-2 text-[11px] leading-snug text-ink-muted font-sans">
                    {img.caption}
                  </figcaption>
                ) : null}
              </figure>
            ) : null}


            <div className="mx-auto mt-10 w-full max-w-3xl">
              {toc.length >= 2 ? (
                <details className="mb-8 border border-hairline bg-card p-5 lg:hidden">
                  <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wide text-brand font-sans">
                    In this article ({toc.length})
                  </summary>
                  <div className="mt-4">
                    <ArticleToc entries={toc} />
                  </div>
                </details>
              ) : null}

              {/* Anonymous readers get a short teaser before the gate so the
                  page still feels like a real article, not a blank wall. */}
              {articleLocked ? (
                <>
                  {previewHtml ? (
                    <div className="relative">
                      <div
                        className="editorial-prose editorial-lede"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                      <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent"
                        aria-hidden="true"
                      />
                    </div>
                  ) : null}
                  <div>
                    <ArticleLimitGate />
                  </div>
                </>
              ) : (
                <>
                  <WhyItMatters bullets={insight?.bullets ?? []} />

                  <TrackerBadge badge={articleContext?.tracker ?? null} />

                  {leadHtml ? (
                    <div
                      className="editorial-prose editorial-lede"
                      dangerouslySetInnerHTML={{ __html: leadHtml }}
                    />
                  ) : null}

                  {pullQuote ? <PullQuote text={pullQuote} /> : null}

                  {restLeadHtml ? (
                    <div
                      className="editorial-prose"
                      dangerouslySetInnerHTML={{ __html: restLeadHtml }}
                    />
                  ) : null}

                  {/* Mobile/tablet in-article placement, the sidebar rail is
                      desktop-only, so small screens would otherwise carry no
                      inventory at all. */}
                  <div className="my-8 lg:hidden">
                    <AdSlot
                      size="mobile-320x100"
                      id="article-inline-mobile"
                      visibilityClassName="flex lg:hidden"
                    />
                  </div>

                  {chunks.slice(1).map((html, i) => (
                    <React.Fragment key={i + 1}>
                      <div className="editorial-prose" dangerouslySetInnerHTML={{ __html: html }} />

                      {/* Small screens get the company dossier inline instead of in the rail. */}
                      {i === 0 && !articleLocked ? (
                        <CompanyContextCard
                          tags={tags}
                          title={title}
                          currentSlug={slug}
                          category={primaryCat ? { name: primaryCat.name, slug: primaryCat.slug } : null}
                          variant="inline"
                        />
                      ) : null}

                      {inlineRelated[i] ? (
                        <InlineRelated post={inlineRelated[i]} fromSlug={slug} position={i} />
                      ) : null}
                    </React.Fragment>
                  ))}

                  <EditorialTake text={insight?.take ?? null} authorName={author?.name ?? null} />

                  <ArticleFaq items={faq} />

                  {insight?.poll ? (
                    <ReaderPulse
                      slug={post.slug}
                      question={insight.poll.question}
                      options={insight.poll.options}
                    />
                  ) : null}
                </>
              )}


              {tags.length ? (
                <div className="sr-only" aria-hidden="false">
                  <p>Tags</p>
                  <ul>
                    {tags.map((t) => (
                      <li key={t.id}>
                        <Link to="/tag/$slug" params={{ slug: t.slug }}>
                          {t.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {author ? (
                <aside className="mt-14 border-t-2 border-foreground pt-8">
                  <p className="category-eyebrow mb-6">About the author</p>
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6">
                    <Link
                      to="/author/$slug"
                      params={{ slug: author.slug }}
                      aria-label={`${author.name}, author profile`}
                      tabIndex={-1}
                      className="block shrink-0 rounded-full outline-none ring-offset-2 ring-offset-background transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand"
                    >
                      {author.avatarUrl ? (
                        <img
                          src={author.avatarUrl}
                          alt={author.name}
                          width={80}
                          height={80}
                          loading="lazy"
                          decoding="async"
                          className="h-20 w-20 rounded-full object-cover ring-1 ring-border"
                        />
                      ) : (
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand text-lg font-bold uppercase text-white">
                          {author.name
                            .split(" ")
                            .map((s) => s[0])
                            .slice(0, 2)
                            .join("")}
                        </div>
                      )}
                    </Link>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-2xl font-bold leading-tight">
                        <Link
                          to="/author/$slug"
                          params={{ slug: author.slug }}
                          className="rounded-sm outline-none hover:text-brand hover:underline focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        >
                          {author.name}
                        </Link>
                      </p>
                      <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-ink-muted font-sans">
                        {author.title ?? "Contributor"} · RetailNews.asia
                      </p>
                      <AuthorBio
                        text={author.description}
                        authorName={author.name}
                        authorSlug={author.slug}
                        authorTitle={author.title}
                        articleSlug={post.slug}
                        articleId={post.id}
                        location="article"
                      />
                    </div>
                  </div>
                </aside>
              ) : null}
            </div>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-32 flex flex-col gap-8">
              {!articleLocked ? (
                <div className="overflow-hidden border border-hairline bg-card shadow-sm">
                  <CompanyContextCard
                    tags={tags}
                    title={title}
                    currentSlug={slug}
                    category={primaryCat ? { name: primaryCat.name, slug: primaryCat.slug } : null}
                    toc={toc.length >= 2 ? toc : undefined}
                  />
                </div>
              ) : null}
              <AdSlot size="sidebar-300x600" priority />

              <BriefInviteCard />

              <AdSlot size="sidebar-300x250" />
              <AdSlot size="sidebar-300x250" id="article-sidebar-mpu-2" />
            </div>
          </aside>
        </article>
      </div>

      {related.length ? (
        <section ref={relatedRailRef} className="hairline-t bg-gradient-to-b from-muted/40 via-background to-background">
          <div className="container-editorial py-16">
            <div className="mb-10 flex items-end justify-between gap-6">
              <div>
                <p className="mb-2 flex items-center gap-3 text-sm font-medium italic text-brand font-serif">
                  <span className="h-px w-8 bg-brand" />
                  Continue reading
                </p>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl font-bold tracking-tight">
                  More from the newsroom
                </h2>
              </div>
              <Link
                to="/"
                className="hidden md:inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-ink-muted hover:text-brand font-sans"
              >
                All coverage
                <span aria-hidden>→</span>
              </Link>
            </div>
            <TitleRule />


            {(() => {
              const items = related.slice(0, 6);
              const [lead, ...rest] = items;
              const side = rest.slice(0, 2);
              const tail = rest.slice(2, 5);
              const leadImg = lead ? featuredImage(lead) : null;
              const leadCat = lead ? postCategories(lead)[0] : null;
              return (
                <div className="grid gap-10 lg:grid-cols-12">
                  {/* Lead */}
                  {lead ? (
                    <Link
                      to="/$slug"
                      params={{ slug: lead.slug }}
                      {...trackRelated("footer-lead", 0, lead)}
                      className="group lg:col-span-7 block"
                    >
                      <div className="relative overflow-hidden">
                        {leadImg ? (
                          <SmartImage
                            src={leadImg.src}
                            srcSet={leadImg.srcSet || undefined}
                            sizes="(min-width: 1280px) 400px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                            alt={leadImg.alt || titleText(lead)}
                            aspect="aspect-[16/10]"
                            imgClassName="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                        ) : (
                          <div className="aspect-[16/10] bg-muted" />
                        )}
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
                        {leadCat ? (
                          <span className="absolute left-4 top-4 bg-brand px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white font-sans">
                            {decodeHtmlEntities(leadCat.name)}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="mt-5 font-serif text-2xl md:text-3xl font-bold leading-[1.15] tracking-tight group-hover:text-brand">
                        {titleText(lead)}
                      </h3>
                      <p className="mt-3 line-clamp-2 text-[15px] leading-relaxed text-ink-muted">
                        {excerptText(lead, 180)}
                      </p>
                      <ArticleMeta post={lead} hideYear showAuthor className="mt-4" />

                    </Link>
                  ) : null}

                  {/* Side stacked with thumbs */}
                  {side.length ? (
                    <div className="lg:col-span-5 flex flex-col divide-y divide-hairline">
                      {side.map((p, si) => {
                        const img = featuredImage(p);
                        const cat = postCategories(p)[0];
                        return (
                          <Link
                            key={p.id}
                            to="/$slug"
                            params={{ slug: p.slug }}
                            {...trackRelated("footer-side", si, p)}
                            className="group grid grid-cols-[128px_1fr] gap-4 py-5 first:pt-0 last:pb-0"
                          >
                            <div className="overflow-hidden">
                              {img ? (
                                <SmartImage
                                  src={img.src}
                                  srcSet={img.srcSet || undefined}
                                  sizes="(min-width: 1280px) 400px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                                  alt={img.alt || titleText(p)}
                                  aspect="aspect-square"
                                  imgClassName="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="aspect-square bg-muted" />
                              )}
                            </div>
                            <div className="flex flex-col justify-between">
                              <div>
                                {cat ? (
                                  <p className="text-[10px] font-bold uppercase tracking-wide text-brand font-sans">
                                    {decodeHtmlEntities(cat.name)}
                                  </p>
                                ) : null}
                                <h3 className="mt-1.5 font-serif text-[17px] font-bold leading-snug tracking-tight group-hover:text-brand">
                                  {titleText(p)}
                                </h3>
                              </div>
                              <ArticleMeta post={p} size="xs" hideYear showAuthor className="mt-3" />

                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}

                  {/* Tail: numbered text list */}
                  {tail.length ? (
                    <div className="lg:col-span-12 hairline-t pt-8 mt-2">
                      <p className="category-eyebrow mb-5">Also worth your time</p>
                      <ol className="grid gap-6 md:grid-cols-3">
                        {tail.map((p, i) => {
                          const cat = postCategories(p)[0];
                          return (
                            <li key={p.id} className="group">
                              <Link
                                to="/$slug"
                                params={{ slug: p.slug }}
                                {...trackRelated("footer-tail", i, p)}
                                className="grid grid-cols-[auto_1fr] gap-4"
                              >
                                <span className="font-serif text-4xl font-bold leading-none text-brand/30 transition-colors group-hover:text-brand">
                                  {String(i + 1).padStart(2, "0")}
                                </span>
                                <div>
                                  {cat ? (
                                    <p className="text-[10px] font-bold uppercase tracking-wide text-ink-muted font-sans">
                                      {decodeHtmlEntities(cat.name)}
                                    </p>
                                  ) : null}
                                  <h3 className="mt-1 font-serif text-[16px] font-bold leading-snug tracking-tight group-hover:text-brand">
                                    {titleText(p)}
                                  </h3>
                                  <ArticleMeta post={p} hideYear showAuthor className="mt-2" />

                                </div>
                              </Link>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  ) : null}
                </div>
              );
            })()}
          </div>
        </section>
      ) : null}

      {/* One contextual next step, chosen from where this reader already is.
          Active paid members see nothing here. */}
      <ConversionModule placement="article_end" />



    </>

  );
}

function ShareButton({ title }: { title: string }) {
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const url = typeof window !== "undefined" ? window.location.href : "";
  const eTitle = encodeURIComponent(title);
  const eUrl = encodeURIComponent(url);

  const onClick = async () => {
    if (typeof navigator !== "undefined" && typeof (navigator as Navigator).share === "function") {
      try {
        await (navigator as Navigator).share({ title, url });
        logShare("native", title);
        return;
      } catch { /* fall through to menu */ }
    }
    setOpen((v) => !v);
  };

  const copy = async () => {
    logShare("copy", title);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback for insecure contexts
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
  };

  const openPopup = (channel: ShareChannel, href: string) => {
    logShare(channel, title);
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=520");
    setOpen(false);
  };

  const linkClass = "flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-ink hover:bg-muted";

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={onClick}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-brand hover:underline font-sans"
      >
        <Share2 className="h-3.5 w-3.5" /> Share
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-52 overflow-hidden rounded-md border border-border bg-background shadow-lg"
        >
          <button className={linkClass} onClick={() => openPopup("twitter", `https://twitter.com/intent/tweet?text=${eTitle}&url=${eUrl}`)}>X / Twitter</button>
          <button className={linkClass} onClick={() => openPopup("linkedin", `https://www.linkedin.com/sharing/share-offsite/?url=${eUrl}`)}>LinkedIn</button>
          <button className={linkClass} onClick={() => openPopup("facebook", `https://www.facebook.com/sharer/sharer.php?u=${eUrl}`)}>Facebook</button>
          <button className={linkClass} onClick={() => openPopup("whatsapp", `https://api.whatsapp.com/send?text=${eTitle}%20${eUrl}`)}>WhatsApp</button>
          <a
            className={linkClass}
            href={`mailto:?subject=${eTitle}&body=${eUrl}`}
            onClick={() => { logShare("email", title); setOpen(false); }}
          >Email</a>
          <button className={linkClass} onClick={copy}>{copied ? "Link copied ✓" : "Copy link"}</button>
        </div>
      )}
    </div>
  );
}

